import { type RefObject,useCallback, useLayoutEffect } from 'react';

/**
 * Measures the remaining vertical viewport space from a target element's
 * top edge to the bottom of the viewport, and sets that as the element's
 * fixed height so native `overflow` can apply and make content scrollable amidst RIO adynamically injected styles.
 *
 * @param containerRef - Ref to the ancestor that contains the target element
 * @param targetSelector - CSS selector to find the scrollable target within the container
 * @param bottomOffset - Optional pixel padding from the viewport bottom (default: 0)
 */
export const useRemainingHeight = (
  containerRef: RefObject<HTMLElement | null>,
  targetSelector: string,
  bottomOffset: number = 0
) => {
  const recalc = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const target = container.querySelector<HTMLElement>(targetSelector);
    if (!target) return;

    const targetRect = target.getBoundingClientRect();
    const remaining = window.innerHeight - targetRect.top - bottomOffset;

    target.style.height = `${Math.max(remaining, 0)}px`;
    target.style.overflowY = 'auto';
  }, [containerRef, targetSelector, bottomOffset]);

  useLayoutEffect(() => {
    recalc();

    const observer = new ResizeObserver(recalc);
    if (containerRef.current) observer.observe(containerRef.current);

    window.addEventListener('resize', recalc);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', recalc);
    };
  }, [recalc]);
};